import React, { useState, useMemo, useEffect } from 'react';
import { isEqual, omit } from 'lodash';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  SelectChangeEvent,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  MEASUREMENT_LEVELS,
  measurementCheckboxItems,
  menuItems,
  performanceCheckboxItems,
} from './constants/ReportsAdmin';
import useReportConfig, { useGetReportConfigDefaults } from '@components/hooks/apis/reportConfig';
import { COST_MODEL, LOOKER_EXPERIENCE } from '@components/AdvertiserContext';
import { useGetAdvertiser } from '@components/hooks/apis/advertisers';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { useDomain } from '@components/hooks';
import { Info } from '@mui/icons-material';

interface CheckBoxSectionProps {
  title: string;
  checkboxes: { name: string; label: string; checked: boolean }[];
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  overrideDefaults: boolean;
  disable?: (value: string) => boolean;
  disabledTooltip?: { [key: string]: string };
  subtext?: { [key: string]: string };
}

const CheckBoxSection = ({
  title,
  checkboxes,
  handleCheckboxChange,
  overrideDefaults,
  disable = () => false,
  disabledTooltip = {},
  subtext,
}: CheckBoxSectionProps) => (
  <Box display="flex" flexDirection="column">
    <Typography
      sx={{
        fontSize: '16px',
        marginBottom: '8px',
        marginTop: '16px',
        fontWeight: 700,
        color: '#13171A',
      }}
    >
      {title}
    </Typography>
    {checkboxes.map((checkbox) => (
      <Box key={checkbox.name}>
        <FormControlLabel
          sx={{ marginRight: 0 }}
          control={
            <Checkbox
              sx={{ padding: '4px', marginLeft: '6px' }}
              checked={checkbox.checked}
              onChange={handleCheckboxChange}
              name={checkbox.name}
              disabled={!overrideDefaults || disable(checkbox.name)}
            />
          }
          label={checkbox.label}
        />
        {disable(checkbox.name) && disabledTooltip[checkbox.name] && (
          <Tooltip title={disabledTooltip[checkbox.name]} placement="right" arrow>
            <Info sx={{ height: '12px', color: 'rgba(0, 0, 0, 0.38)' }} />
          </Tooltip>
        )}
        <Box>
          {subtext && subtext[checkbox.name] && (
            <Box
              marginLeft="28px"
              marginBottom="16px"
              fontSize="12px"
              color={overrideDefaults ? "#5C6B73" : 'rgba(0, 0, 0, 0.38)'}
            >
              {subtext[checkbox.name]}
            </Box>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

interface FormValues {
  measurementLevel: string;
  campaignPerformance: boolean;
  pathToPurchase: boolean;
  postConversions: boolean;
  spikeLift: boolean;
  alwaysOnIncrementality: boolean;
}

const ReportsAdmin = ({ currentAccount }: { currentAccount: { ad_account_id: number } }) => {
  const domain = useDomain();
  const [reportConfigFetched, setReportConfigFetched] = useState(false);
  const [reportConfigCreated, setReportConfigCreated] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [override, setOverride] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    measurementLevel: MEASUREMENT_LEVELS.LEVEL_1,
    campaignPerformance: false,
    pathToPurchase: false,
    postConversions: false,
    spikeLift: false,
    alwaysOnIncrementality: false,
  });

  const { data: reportConfigDefaults } = useGetReportConfigDefaults(currentAccount.ad_account_id);
  const { data: advertiser, isLoading } = useGetAdvertiser(currentAccount.ad_account_id);
  const { data, error, mutate, isValidating } = useReportConfig.get(currentAccount.ad_account_id);
  const { postReportConfig } = useReportConfig.post({
    onSuccess: () => mutate(),
    accountId: currentAccount.ad_account_id,
  });
  const { patchReportConfig } = useReportConfig.patch({
    accountId: currentAccount.ad_account_id,
    onSuccess: () => mutate(),
  });

  // Create a new report config if not found
  useEffect(() => {
    if (error?.response?.status === 404 && !reportConfigCreated) {
      postReportConfig({});
      setReportConfigCreated(true);
    }
  }, [error, postReportConfig, reportConfigCreated]);

  useEffect(() => {
    if (!reportConfigFetched || !data) return;
    const dataWithoutAdAccount = omit(data, 'adAccount');
    setSettingsChanged(!isEqual(dataWithoutAdAccount, formValues));
  }, [formValues, reportConfigFetched, data]);

  useEffect(() => {
    if (!override && reportConfigDefaults) {
      setFormValues(reportConfigDefaults[formValues.measurementLevel]);
    }
  }, [override, reportConfigDefaults]);

  useEffect(() => {
    if (!data || reportConfigFetched || currentAccount.ad_account_id !== data.adAccount || !reportConfigDefaults) return;

    const defaults = reportConfigDefaults[data.measurementLevel];
    const dataWithoutAdAccount = omit(data, 'adAccount');
    setOverride(domain.peacock || !isEqual(dataWithoutAdAccount, defaults));

    setFormValues({
      measurementLevel: data.measurementLevel,
      campaignPerformance: data.campaignPerformance,
      pathToPurchase: data.pathToPurchase,
      postConversions: data.postConversions,
      spikeLift: data.spikeLift,
      alwaysOnIncrementality: data.alwaysOnIncrementality,
    });

    setReportConfigFetched(true);
  }, [data, reportConfigFetched, currentAccount.ad_account_id, reportConfigDefaults, domain.peacock, reportConfigDefaults]);

  const handleDropdownChange = (event: SelectChangeEvent) => {
    setFormValues(reportConfigDefaults[event.target.value]);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      [event.target.name]: event.target.checked,
    }));
  };

  const handleOverrideToggle = () => {
    setOverride((prev) => {
      const newOverride = !prev;
      if (!newOverride) {
        setFormValues(reportConfigDefaults[formValues.measurementLevel]);
      } else if (data) {
        if (data.measurementLevel !== formValues.measurementLevel) return newOverride;
        setFormValues({
          measurementLevel: data.measurementLevel,
          campaignPerformance: data.campaignPerformance,
          pathToPurchase: data.pathToPurchase,
          postConversions: data.postConversions,
          spikeLift: data.spikeLift,
          alwaysOnIncrementality: data.alwaysOnIncrementality,
        });

      }
      return newOverride;
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (settingsChanged) {
      patchReportConfig(formValues);
      setSettingsChanged(false);
    }
  };

  const generateCheckboxes = (checkboxes: { name: string; label: string }[]) =>
    checkboxes.map((checkbox) => ({
      ...checkbox,
      checked: !!formValues[checkbox.name as keyof typeof formValues],
    }));

  const performanceCheckboxes = useMemo(() =>
    generateCheckboxes(performanceCheckboxItems)
    , [formValues]);

  const measurementCheckboxes = useMemo(() =>
    generateCheckboxes(measurementCheckboxItems)
    , [formValues]);

  if (isValidating || isLoading) {
    return <LoadingSpinner />;
  }

  const getParenthesisText = (isGaming: boolean) => (isGaming ? ' (Gaming)' : '(Default)');

  const campaignPerformanceSubtext = () => {
    if (!advertiser || !formValues) return '';
    const { cost_model, looker_experience } = advertiser;
    const { measurementLevel } = formValues;

    if (cost_model === COST_MODEL.CPA) {
      return 'CPA: Performance (CPA) tab only';
    }
    if (measurementLevel === MEASUREMENT_LEVELS.LEVEL_1) {
      return 'CPM, Level 1: Delivery tab only';
    }
    const parenthesisText = getParenthesisText(looker_experience === LOOKER_EXPERIENCE.GAMING);
    return `CPM ${parenthesisText}, Level 2+: Delivery, Performance ${parenthesisText.toLowerCase()}, Inv & Geo ${parenthesisText.toLowerCase()} tabs`;
  };

  return (
    <Box marginLeft="16px">
      <Typography
        sx={{
          fontSize: '24px',
          marginBottom: '24px',
          fontWeight: 700,
          color: '#13171A',
        }}
      >
        Report Availability
      </Typography>
      <form onSubmit={handleSubmit}>
        {!domain.peacock && (
          <Box display="flex" marginBottom="16px">
            <FormControl fullWidth sx={{ width: '420px' }}>
              <InputLabel id="measurement-level-label" color="secondary">
                Measurement Level
              </InputLabel>
              <Select
                labelId="measurement-level-label"
                id="measurement-level"
                value={formValues.measurementLevel}
                label="Measurement Level"
                onChange={handleDropdownChange}
                color="secondary"
              >
                {menuItems.map((item) => (
                  <MenuItem key={item.value} value={item.value} data-testid={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box display="flex" alignItems="center" marginLeft="16px">
              <Switch
                data-testid="override-switch"
                checked={override}
                onClick={handleOverrideToggle}
              />
              <Typography sx={{ fontSize: '16px' }}>Override Defaults</Typography>
            </Box >
          </Box >
        )}
        <CheckBoxSection
          title="Performance Reports"
          checkboxes={performanceCheckboxes}
          handleCheckboxChange={handleCheckboxChange}
          overrideDefaults={override}
          disable={(value) => value === 'postConversions' && !advertiser?.has_sequential_events}
          disabledTooltip={{
            postConversions:
              'This report can only be enabled for Ad Accounts that have already configured Sequential Events in the Admin Panel',
          }}
          subtext={{
            campaignPerformance: campaignPerformanceSubtext(),
          }}
        />
        <CheckBoxSection
          title="Measurement Reports"
          checkboxes={measurementCheckboxes}
          handleCheckboxChange={handleCheckboxChange}
          overrideDefaults={override}
        />
        <Box display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            disabled={!settingsChanged}
            sx={{
              width: '203px',
              height: '60px',
              borderRadius: '8px',
              fontSize: '20px',
              fontWeight: 700,
              marginTop: '24px',
              backgroundColor: settingsChanged ? '#1DAFFF' : '#B0BEC5',
            }}
          >
            Save Settings
          </Button>
        </Box>
      </form >
    </Box >
  );
};

export default ReportsAdmin;
